import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CartContext = createContext();

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

export const CartProvider = ({ children }) => {
    const [cart, setCart] = useState({ items: [], total: 0 });
    const [cartId, setCartId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [appliedDiscount, setAppliedDiscount] = useState(null);
    const [holdExpired, setHoldExpired] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => {
        const storedCartId = localStorage.getItem('cartId');
        const storedDiscount = localStorage.getItem('appliedDiscount');
        
        if (storedDiscount) {
            try {
                setAppliedDiscount(JSON.parse(storedDiscount));
            } catch (e) {
                localStorage.removeItem('appliedDiscount');
            }
        }
        
        if (storedCartId) {
            setCartId(storedCartId);
            fetchCart(storedCartId);
        } else {
            createNewCart();
        }
    }, []);

    // Start hold expiry timer when cart items change
    useEffect(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }
        
        const itemsWithHolds = cart.items.filter(function(item) {
            return item.hold_expires_at;
        });
        
        if (itemsWithHolds.length > 0) {
            timerRef.current = setInterval(function() {
                var now = new Date().toISOString();
                var expired = itemsWithHolds.some(function(item) {
                    return item.hold_expires_at && item.hold_expires_at <= now;
                });
                if (expired) {
                    setHoldExpired(true);
                    if (cartId) {
                        fetchCart(cartId);
                    }
                }
            }, 1000);
        }
        
        return function() {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [cart.items, cartId]);

    const createNewCart = async () => {
        try {
            const response = await axios.post(API + '/cart/create');
            const newCartId = response.data.id;
            localStorage.setItem('cartId', newCartId);
            setCartId(newCartId);
            setCart({ items: [], total: 0 });
            setHoldExpired(false);
        } catch (error) {
            var fallbackId = 'cart_' + Date.now();
            localStorage.setItem('cartId', fallbackId);
            setCartId(fallbackId);
        }
    };

    const fetchCart = async (id) => {
        try {
            setLoading(true);
            const response = await axios.get(API + '/cart/' + id);
            var items = response.data.items || [];
            setCart({
                items: items,
                total: response.data.total || 0
            });
            // Check if any items were removed due to expired holds
            if (items.length === 0 && holdExpired) {
                setHoldExpired(true);
            }
        } catch (error) {
            setCart({ items: [], total: 0 });
        } finally {
            setLoading(false);
        }
    };

    const addToCart = useCallback(async (sessionId, slotId) => {
        if (!cartId) return { success: false, error: 'Cart not initialized' };
        
        try {
            setLoading(true);
            setHoldExpired(false);
            const response = await axios.post(API + '/cart/add', {
                cart_id: cartId,
                session_id: sessionId,
                slot_id: slotId
            });
            
            await fetchCart(cartId);
            
            return { success: true, data: response.data };
        } catch (error) {
            return { 
                success: false, 
                error: error.response?.data?.detail || 'Failed to add to cart' 
            };
        } finally {
            setLoading(false);
        }
    }, [cartId]);

    const removeFromCart = useCallback(async (bookingId) => {
        if (!cartId) return { success: false, error: 'Cart not initialized' };
        
        try {
            setLoading(true);
            await axios.delete(API + '/cart/' + cartId + '/item/' + bookingId);
            await fetchCart(cartId);
            return { success: true };
        } catch (error) {
            return { 
                success: false, 
                error: error.response?.data?.detail || 'Failed to remove from cart' 
            };
        } finally {
            setLoading(false);
        }
    }, [cartId]);

    const clearCart = useCallback(async () => {
        setAppliedDiscount(null);
        localStorage.removeItem('appliedDiscount');
        setHoldExpired(false);
        await createNewCart();
    }, []);

    const applyDiscount = useCallback(async (code) => {
        if (!code) {
            setAppliedDiscount(null);
            localStorage.removeItem('appliedDiscount');
            return { success: true };
        }
        
        try {
            const response = await axios.post(API + '/discount/apply', {
                code: code,
                cart_total: cart.total
            });
            
            if (response.data.valid) {
                var discountData = {
                    code: response.data.code,
                    discount_type: response.data.discount_type,
                    discount_value: response.data.discount_value,
                    discount_amount: response.data.discount_amount,
                    original_total: response.data.original_total,
                    new_total: response.data.new_total
                };
                setAppliedDiscount(discountData);
                localStorage.setItem('appliedDiscount', JSON.stringify(discountData));
                return { success: true, data: discountData };
            } else {
                return { success: false, error: 'Invalid discount code' };
            }
        } catch (error) {
            return { 
                success: false, 
                error: error.response?.data?.detail || 'Failed to apply discount' 
            };
        }
    }, [cart.total]);

    const removeDiscount = useCallback(() => {
        setAppliedDiscount(null);
        localStorage.removeItem('appliedDiscount');
    }, []);

    const dismissHoldExpired = useCallback(() => {
        setHoldExpired(false);
    }, []);

    const checkout = useCallback(async (discountCode) => {
        if (!cartId || cart.items.length === 0) {
            return { success: false, error: 'Cart is empty' };
        }
        
        try {
            setLoading(true);
            var requestData = {
                cart_id: cartId,
                origin_url: window.location.origin
            };
            
            var codeToUse = discountCode || (appliedDiscount ? appliedDiscount.code : null);
            if (codeToUse) {
                requestData.discount_code = codeToUse;
            }
            
            const response = await axios.post(API + '/checkout', requestData);
            
            return { success: true, url: response.data.url, sessionId: response.data.session_id };
        } catch (error) {
            var errorMsg = error.response?.data?.detail || 'Failed to create checkout';
            // If hold expired during checkout attempt
            if (errorMsg.includes('expired')) {
                setHoldExpired(true);
                await fetchCart(cartId);
            }
            return { 
                success: false, 
                error: errorMsg
            };
        } finally {
            setLoading(false);
        }
    }, [cartId, cart.items.length, appliedDiscount]);

    var finalTotal = appliedDiscount ? appliedDiscount.new_total : cart.total;
    var itemCount = cart.items.length;

    var value = {
        cart: cart,
        cartId: cartId,
        loading: loading,
        itemCount: itemCount,
        finalTotal: finalTotal,
        appliedDiscount: appliedDiscount,
        holdExpired: holdExpired,
        addToCart: addToCart,
        removeFromCart: removeFromCart,
        clearCart: clearCart,
        applyDiscount: applyDiscount,
        removeDiscount: removeDiscount,
        checkout: checkout,
        dismissHoldExpired: dismissHoldExpired,
        refreshCart: function() { if (cartId) fetchCart(cartId); }
    };

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};
